import React from 'react';
import PluginHeader from '../PluginHeader';
import { BASE_URL } from '../Api';
import { Helmet, HelmetProvider } from "react-helmet-async";

const PriceListsPlugin = () => {
    return (
        <>
            <HelmetProvider>
                <Helmet>
                    <title>Price lists - Aronium</title>
                    <meta name="description" content="Use different price lists for for customers and improve sales efficiency with Price lists plugin" />
                </Helmet>
            </HelmetProvider>

            {/* Hero Section */}
            <section className="py-8 max-w-screen-xl px-4 mx-auto">
                <div className="max-w-screen-xl mx-auto text-center">
                    <h1 className="text-4xl mb-4">Price lists</h1>
                    <p>
                        Assign different price lists to customers and cash registers, catering to both retail and wholesale markets.
                    </p>

                    {/* Call to Action Buttons */}
                    <div className="mt-8 sm:mt-12 flex flex-col sm:flex-row justify-center gap-4 sm:gap-6">
                        {/* Download for Aronium Lite Button */}
                        <a
                            href={`${BASE_URL}/plugins/price-lists/latest/Aronium.Pos.PriceLists.Lite.exe`}
                            className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-full text-gray-900 bg-[#B8A68C] hover:bg-[#D0B89D] transition duration-300 shadow-lg focus:ring-4 focus:ring-brown-200 focus:outline-none hover:no-underline"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                                />
                            </svg>
                            Download for Aronium Lite
                        </a>

                        {/* Download for Aronium Pro Button */}
                        <a
                            href={`${BASE_URL}/plugins/price-lists/latest/Aronium.Pos.PriceLists.Pro.exe`}
                            className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-full text-gray-900 bg-[#A3BCC7] hover:bg-[#C3D1D8] transition duration-300 shadow-lg focus:ring-4 focus:ring-gray-300 focus:outline-none hover:no-underline"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                                />
                            </svg>
                            Download for Aronium Pro
                        </a>
                    </div>
                </div>
            </section>

            <div className="flex flex-col text-center mt-10">
                <div className="grid place-content-center justify-items-center px-4">
                    <img src="/images/price-lists.png" className='border-t-8 border-l border-r rounded-t-xl border-solid border-gray-300 xl:max-w-6xl' alt="Price lists management" />
                </div>
            </div>
            <div className="py-16 bg-gradient-to-tr from-[#C3D1D8] to-[#A3BCC7]">
                <PluginHeader
                    price="5" />
            </div>

            {/* Overview Section */}
            <div className="relative py-20 bg-gradient-to-r from-gray-50 to-white">
                <div className="relative max-w-7xl mx-auto text-center px-8 lg:px-12 text-gray-600">
                    <h2 className="text-4xl text-gray-800 leading-tight mb-8">
                        Why use <span className='font-semibold'>Price lists</span> plugin?
                    </h2>
                    <p className="text-lg max-w-5xl mx-auto m-4">
                        The <span className='font-semibold'>Price lists</span> plugin enables businesses to create and manage multiple pricing strategies tailored to various customer segments or sales channels. This flexibility allows for personalized pricing, enhancing customer satisfaction and loyalty.
                    </p>
                </div>
            </div>

            {/* Key Features Section */}
            <div className="bg-gray-100 py-24">
                <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
                    {/* Feature Cards */}
                    <div className="bg-white p-8 shadow-lg rounded-lg border">

                        <h3 className="text-2xl font-semibold text-gray-800">
                            Assigning price lists
                        </h3>
                        <p className="mt-4 text-gray-600">
                            Easily assign specific price lists to individual customers or cash registers, ensuring the appropriate pricing is applied during sales transactions.
                        </p>
                    </div>
                    <div className="bg-white p-8 shadow-lg rounded-lg border">
                        <h3 className="text-2xl font-semibold text-gray-800">
                            Seamless price modification
                        </h3>
                        <p className="mt-4 text-gray-600">
                            Update prices across all products or specific groups with just a few clicks. Implement percentage-based changes or copy existing price lists as templates to efficiently manage pricing.
                        </p>
                    </div>

                </div>
            </div>

            {/* Call to Action Section */}
            <div className="relative bg-gradient-to-tr from-[#5B5F5E] to-[#8E8C89] py-24 text-center text-white">
                <div className="relative max-w-6xl mx-auto px-4">
                    <h2 className="text-4xl xl:text-5xl mb-6">
                        Ready to customize your pricing strategy?
                    </h2>
                    <p className="text-xl mb-10">
                        Start using the <span className='font-semibold'>Price lists</span> plugin to make pricing adjustments and offer personalized rates with ease.
                    </p>
                    <a
                        href="https://help.aronium.com/hc/en-us/articles/14091509136146-Manage-price-lists"
                        className="button-rounded-white px-12 py-4"
                        target="_blank"
                        rel="noreferrer"
                    >
                        Learn more <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="ml-2 w-6 h-6 transition-all">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
                        </svg>
                    </a>
                </div>
            </div>
        </>
    );
};

export default PriceListsPlugin;
